import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import config from '../../../config/apiConfig';

const ModalCapturaCliente = ({ isVisible, onClose, onConfirmar, esDelivery, sectores = [], totalPedido, isLoaded, usaProgramados, capacidadDiaria = 0 }) => {
    const [formData, setFormData] = useState({
        telefonoCliente: '', nombreCliente: '', direccionEnvio: '',
        sector: '', id_sector: null, observacion: '', costoEnvio: 0, 
        lugar_consumo: 'LLEVAR', lat: null, lng: null,
        fecha_programada: '', es_programado: false
    });

    const [autocomplete, setAutocomplete] = useState(null);
    const [buscando, setBuscando] = useState(false);
    const [errorZona, setErrorZona] = useState(null);
    const [errorCapacidad, setErrorCapacidad] = useState(null);
    const [verificandoCapacidad, setVerificandoCapacidad] = useState(false);
    const telefonoRef = useRef(null);

    // 🚩 FUNCIÓN CLAVE: Limpia puntos, comas y asegura que sea un NÚMERO real
    const limpiarNumero = (val) => {
        if (!val) return 0;
        const limpio = String(val).replace(/[^0-9.-]+/g, "");
        return parseFloat(limpio) || 0;
    };

    useEffect(() => {
        if (isVisible) {
            setFormData({
                telefonoCliente: '', nombreCliente: '', direccionEnvio: '',
                sector: '', id_sector: null, observacion: '', costoEnvio: 0, 
                lugar_consumo: 'LLEVAR', lat: null, lng: null,
                fecha_programada: '', es_programado: false
            });
            setErrorZona(null);
            setTimeout(() => { if (telefonoRef.current) telefonoRef.current.focus(); }, 300);
        }
    }, [isVisible]);

    const ejecutarValidacionDeZona = (lat, lng, direccionParaMostrar) => {
        if (!window.google || !window.google.maps.geometry) return;

        const punto = new window.google.maps.LatLng(parseFloat(lat), parseFloat(lng));
        let sectorEncontrado = null;

        sectores.forEach(s => {
            if (s.poligono) {
                const area = new window.google.maps.Polygon({ paths: s.poligono });
                if (window.google.maps.geometry.poly.containsLocation(punto, area)) {
                    sectorEncontrado = s;
                }
            }
        });

        if (sectorEncontrado) {
            setFormData(prev => ({
                ...prev,
                direccionEnvio: direccionParaMostrar,
                sector: sectorEncontrado.nombre_sector,
                id_sector: sectorEncontrado.id,
                costoEnvio: limpiarNumero(sectorEncontrado.valor_entrega), // 🚩 Aseguramos número
                lat: lat,
                lng: lng
            }));
            setErrorZona(null);
        } else {
            setFormData(prev => ({
                ...prev,
                direccionEnvio: direccionParaMostrar,
                lat: lat, lng: lng, id_sector: null, costoEnvio: 0
            }));
            setErrorZona("⚠️ Dirección fuera de zona de reparto.");
        }
    };

    const buscarCliente = async () => {
        const fono = String(formData.telefonoCliente).trim();
        if (!fono || fono.length < 4) return;

        setBuscando(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${config.api_url}/api/clientes/buscar/${fono}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'token': token }
            });
            const data = await response.json();

            if (data.res && data.data) {
                const c = data.data;
                setFormData(prev => ({
                    ...prev,
                    nombreCliente: c.cliente_nombre || '',
                    direccionEnvio: c.cliente_direccion || '',
                    lat: c.lat,
                    lng: c.lng
                }));

                if (c.lat && c.lng) {
                    ejecutarValidacionDeZona(c.lat, c.lng, c.cliente_direccion);
                }
            }
        } catch (error) { console.error(error); } 
        finally { setBuscando(false); }
    };

    const alCambiarDireccionGoogle = () => {
        if (autocomplete !== null) {
            const place = autocomplete.getPlace();
            if (!place.geometry) return;
            ejecutarValidacionDeZona(place.geometry.location.lat(), place.geometry.location.lng(), place.formatted_address);
        }
    };

    // 🚩 Verificar capacidad diaria antes de confirmar un pedido programado
    const verificarCapacidad = async (fecha) => {
        if (!fecha || capacidadDiaria <= 0) return true; // Sin límite
        
        setVerificandoCapacidad(true);
        setErrorCapacidad(null);
        try {
            const token = localStorage.getItem('token');
            const soloFecha = fecha.split('T')[0]; // Extraer YYYY-MM-DD
            const response = await fetch(`${config.api_url}/api/pedidos/programados/contar?fecha=${soloFecha}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'token': token }
            });
            const data = await response.json();
            
            if (data.completado && data.total >= capacidadDiaria) {
                setErrorCapacidad(`⚠️ Límite diario alcanzado para esta fecha (${capacidadDiaria} pedidos). Selecciona otro día.`);
                return false;
            }
            setErrorCapacidad(null);
            return true;
        } catch (error) {
            console.error("Error verificando capacidad:", error);
            return true; // Si falla la verificación, permitir continuar
        } finally {
            setVerificandoCapacidad(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (esDelivery && !formData.id_sector) return alert("Dirección inválida o fuera de zona.");
        if (!formData.nombreCliente) return alert("Nombre obligatorio");
        
        // 🚩 Verificar capacidad si es programado
        if (formData.es_programado && formData.fecha_programada) {
            const ok = await verificarCapacidad(formData.fecha_programada);
            if (!ok) return;
        }
        
        onConfirmar(formData);
    };

    if (!isVisible) return null;

    // 🚩 MATEMÁTICA SEGURA PARA EL TOTAL
    const subtotal = limpiarNumero(totalPedido);
    const envio = limpiarNumero(formData.costoEnvio);
    const totalFinal = subtotal + envio;

    return (
        <div className="position-fixed w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ zIndex: 1400, top: 0, left: 0, backgroundColor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(5px)' }}>

            <style>{`.pac-container { z-index: 9999 !important; }`}</style>

            <div className="bg-white rounded shadow-lg overflow-hidden" style={{ width: '500px', maxWidth: '95%' }}>
                <div className="bg-primary text-white p-3 d-flex justify-content-between align-items-center">
                    <h5 className="m-0 font-weight-bold">{esDelivery ? 'Datos de Envío' : 'Pedido Retiro'}</h5>
                    <button className="btn btn-sm btn-outline-light border-0" onClick={onClose}>&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="p-4">
                    <div className="row mb-3">
                        <div className="col-6">
                            <label className="small font-weight-bold">TELÉFONO</label>
                            <input ref={telefonoRef} type="tel" className="form-control" value={formData.telefonoCliente}
                                onChange={(e) => setFormData({ ...formData, telefonoCliente: e.target.value })} 
                                onBlur={buscarCliente}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); buscarCliente(); } }}
                            />
                        </div>
                        <div className="col-6">
                            <label className="small font-weight-bold">NOMBRE</label>
                            <input type="text" className="form-control" value={formData.nombreCliente}
                                onChange={(e) => setFormData({ ...formData, nombreCliente: e.target.value })} />
                        </div>
                    </div>

                    {esDelivery && (
                        <div className="mb-3">
                            <label className="small font-weight-bold text-primary">DIRECCIÓN (GOOGLE)</label>
                            {isLoaded ? (
                                <Autocomplete onLoad={setAutocomplete} onPlaceChanged={alCambiarDireccionGoogle} options={{ componentRestrictions: { country: "cl" } }}>
                                    <input type="text" className="form-control border-primary" placeholder="Buscar calle y número..." 
                                           value={formData.direccionEnvio} onChange={(e) => setFormData({...formData, direccionEnvio: e.target.value})} />
                                </Autocomplete>
                            ) : <small>Cargando Google...</small>}

                            {formData.sector && (
                                <div className="mt-2 p-2 bg-success text-white rounded small font-weight-bold shadow-sm">
                                    ✅ ZONA: {formData.sector.toUpperCase()} (+${envio.toLocaleString('es-CL')})
                                </div>
                            )}
                            {errorZona && <div className="mt-2 p-2 bg-danger text-white rounded small font-weight-bold">{errorZona}</div>}
                        </div>
                    )}

                    <div className="mb-3">
                        <label className="small font-weight-bold">OBSERVACIONES / DEPTO</label>
                        <input type="text" className="form-control" value={formData.observacion} onChange={(e) => setFormData({ ...formData, observacion: e.target.value })} />
                    </div>

                    {/* 🚩 Pedidos programados (solo si está habilitado) */}
                    {usaProgramados && (
                        <div className="mb-3 border-top pt-3">
                            <label className="small font-weight-bold text-info">
                                📅 PROGRAMAR ENTREGA
                            </label>
                            <div className="d-flex gap-2 mt-1">
                                <button 
                                    type="button"
                                    className={`btn btn-sm flex-fill ${!formData.es_programado ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setFormData({...formData, es_programado: false, fecha_programada: ''})}
                                >
                                    ⏺ Ahora (inmediato)
                                </button>
                                <button 
                                    type="button"
                                    className={`btn btn-sm flex-fill ${formData.es_programado ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setFormData({...formData, es_programado: true})}
                                >
                                    📆 Programar
                                </button>
                            </div>
                            
                            {formData.es_programado && (
                                <div className="mt-2">
                                    <input 
                                        type="datetime-local" 
                                        className="form-control"
                                        value={formData.fecha_programada}
                                        onChange={(e) => {
                                            setFormData({...formData, fecha_programada: e.target.value});
                                            setErrorCapacidad(null);
                                        }}
                                        min={new Date().toISOString().slice(0, 16)}
                                    />
                                    <small className="text-muted">Selecciona fecha y hora de entrega</small>
                                    {capacidadDiaria > 0 && (
                                        <div className="mt-1 small text-muted">
                                            🏭 Capacidad diaria: {capacidadDiaria} pedidos
                                        </div>
                                    )}
                                    {errorCapacidad && (
                                        <div className="mt-1 p-2 bg-danger text-white rounded small font-weight-bold">
                                            {errorCapacidad}
                                        </div>
                                    )}
                                    {verificandoCapacidad && (
                                        <div className="mt-1 small text-info">
                                            ⏳ Verificando disponibilidad...
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 🚩 DETALLE DEL TOTAL CORREGIDO */}
                    <div className="alert alert-secondary text-right">
                        <div className="small text-muted">Subtotal: ${subtotal.toLocaleString('es-CL')}</div>
                        {envio > 0 && <div className="small text-danger font-weight-bold">+ Envío: ${envio.toLocaleString('es-CL')}</div>}
                        <h3 className="m-0 font-weight-bold mt-1 text-dark">TOTAL: ${totalFinal.toLocaleString('es-CL')}</h3>
                    </div>

                    <button type="submit" className="btn btn-primary btn-block btn-lg rounded-pill shadow font-weight-bold">
                        CONFIRMAR PEDIDO
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ModalCapturaCliente;